import { defineStore } from 'pinia'
import { watch } from 'vue'
import { createListLoader } from '@/libs/api/createListLoader'
import { wallet } from '@/libs/api'
import { isEqualNumber } from '@/libs/functions'

const WAIT_FOR_WALLET_TIMEOUT = 15000
const loadWallets = createListLoader(wallet.getList, {
  expand: 'currency',
  'per-page': 50,
})

/**
 * @typedef {Object} Bank
 * @property {string} accountHolderName
 * @property {string} name
 * @property {string} address
 */

/**
 * @typedef {Object} WalletDataDetails
 * @property {string} id
 * @property {boolean} isMain
 * @property {string} label
 * @property {string} [iban] - The IBAN for international accounts, optional; present when `type` is "BANK" and the account type is international
 * @property {string} [bicSwift] - The BIC/SWIFT code, optional; present when `type` is "BANK" and the account type is international
 * @property {string} [accountNumber] - The local account number, optional; present when `type` is "BANK" and the account type is local
 * @property {string} [sortCode] - The sort code for local accounts, optional; present when `type` is "BANK" and the account type is local
 * @property {Bank} bank
 */

/**
 * @typedef {Object} CardWalletDataDetails
 * @property {number} ordered
 * @property {number} dispatched
 * @property {number} active
 * @property {number} blocked
 * @property {number} closed
 */

/**
 * @typedef {Object} WalletData
 * @property {string} type - The type of wallet (e.g., "CARD", "BANK")
 * @property {string} [accountId] - The account identifier, may be absent if the type is "CARD"
 * @property {WalletDataDetails[]} details - Account details, may be absent if the type is "CARD"
 * @property {CardWalletDataDetails} [count] - Only for "CARD" type, an object containing information about the number of cards in different states
 */

/**
 * @typedef WalletFlags
 * @property {boolean} isVisible
 * @property {boolean} isFavourite
 */

/**
 * @typedef WalletPermissions
 * @property {boolean} canPay
 * @property {boolean} canCryptoBuy
 * @property {boolean} canCryptoSell
 * @property {boolean} canTopUp
 * @property {boolean} canTopUpFromWallet
 * @property {boolean} canCurrencyExchange
 * @property {boolean} canCardCreate
 * @property {boolean} canCardChipAndPinOrder
 * @property {boolean} canCardVirtualOrder
 * @property {boolean} canCardDisposableOrder
 * @property {boolean} canTransferToCard
 * @property {boolean} canExportCSV
 * @property {boolean} canExportPDF
 * @property {boolean} canExportXML
 * @property {boolean} canClose
 * @property {boolean} canDepositFromOpenBanking
 */

/**
 * @class Wallet

 * @property {number} id
 * @property {string} uuid
 * @property {number} balance
 * @property {string} currencyId
 * @property {string} description
 * @property {WalletFlags} flags
 * @property {WalletPermissions} permissions
 * @property {WalletData} data
 * @property {string} status
 * @property {string} color
 * @property {string} name
 * @property {number[]} transferSourceWalletId
 * @property {number[]} transferTargetWalletId
 * @property {number} updatedAt
 */

export class Wallet {
  constructor(model) {
    Object.assign(this, model)
  }

  /**
   * Get real name of wallet
   * @description description || name
   * @return {string}
   */
  get vName() {
    return this.description || this.name
  }
}

export const useWallets = defineStore('wallets', {
  state: () => ({
    /**
     * @type Wallet[]
     */
    items: [],
    loadProgress: null,
  }),
  actions: {
    /**
     * Load all wallets
     * @returns {Promise<void>}
     */
    getList() {
      return loadWallets(this, (items) => {
        this.items = items.map((item) => new Wallet(item))
      })
    },
    /**
     * Set wallet in list (add if missing, update if exists).
     * @param {Wallet|object} payload
     */
    setWallet(payload) {
      const walletModel = new Wallet(payload)

      const index = this.items.findIndex((item) =>
        isEqualNumber(item.id, walletModel.id),
      )

      if (index === -1) {
        this.items.push(walletModel)
        return
      }

      Object.assign(this.items[index], walletModel)
    },

    /**
     * Wait until wallet appears in list.
     * @param {number|string} id
     * @param {number} timeout
     * @returns {Promise<Wallet>}
     */
    waitForWalletById(id, timeout = WAIT_FOR_WALLET_TIMEOUT) {
      const currentWallet = this.getWalletById(id)

      if (currentWallet) {
        return Promise.resolve(currentWallet)
      }

      return new Promise((resolve, reject) => {
        let timeoutId = null

        const stop = watch(
          () => this.getWalletById(id),
          (walletModel) => {
            if (!walletModel) {
              return
            }

            stop()
            clearTimeout(timeoutId)
            resolve(walletModel)
          },
        )

        timeoutId = setTimeout(() => {
          stop()
          reject(new Error(`Wallet ${id} was not added to store`))
        }, timeout)
      })
    },
  },
  getters: {
    getWalletById(state) {
      return (id) => state.items.find((i) => isEqualNumber(i.id, id))
    },
    // Cache the UUID index to avoid scanning all wallets for each list item.
    // Looking up every wallet takes O(n) overall instead of O(n²).
    walletsByUuid(state) {
      return new Map(state.items.map((wallet) => [wallet.uuid, wallet]))
    },
    getWalletByUuid() {
      return (uuid) => this.walletsByUuid.get(uuid)
    },
    getWalletsFiltered(state) {
      return ({ search }) =>
        state.items.filter((i) => {
          let string = [
            i.vName,
            i.balance,
            i.balance.toString().replace('.', ','), // search of values 0.00 and 0,00
            i.currencyId,
          ]
            .join('')
            .toLowerCase()
          let needle = search.toLowerCase()
          return string.includes(needle)
        })
    },
  },
})
